import PropTypes from "prop-types";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDoubleLeft,
  ChevronDoubleRight,
} from "../../icons/svg/outline";

/**
 * NavigationButton component renders a styled navigation button with chevron icons
 * Used for navigation elements like previous/next week/day buttons
 *
 * @component
 *
 * @param {string} direction - The direction of the chevron: 'left' or 'right'
 * @param {boolean} [double=false] - Whether to use double chevron icons
 * @param {function} onClick - The function to be called when the button is clicked
 * @param {string} [variant='primary'] - The styling variant of the button
 * @param {boolean} [disabled=false] - Whether the button is disabled
 * @param {string} [className] - Additional classes to be added to the button
 * @param {object} [props] - Additional props to be passed to the button
 *
 * @example
 * return (
 *   <NavigationButton
 *     direction="left"
 *     double={true}
 *     onClick={goToPreviousWeek}
 *     variant="primary"
 *     disabled={isDisabled}
 *   />
 * )
 */
const NavigationButton = ({
  direction,
  double = false,
  onClick,
  variant = "primary",
  disabled = false,
  className = "",
  ...props
}) => {
  const variantClasses = {
    primary: "bg-green-700 hover:bg-green-600 text-white",
    secondary: "bg-gray-500 hover:bg-gray-400 text-white",
    outline:
      "border-2 border-green-700 text-green-700 hover:bg-green-700 hover:text-white",
    disabled:
      "bg-gray-300 text-gray-500 cursor-not-allowed pointer-events-none",
  };

  const baseClasses =
    "px-4 py-2 rounded flex items-center justify-center transition-all ease-in-out duration-300 cursor-pointer";

  // Select the appropriate icon based on direction and double props
  const IconComponent = double
    ? direction === "left"
      ? ChevronDoubleLeft
      : ChevronDoubleRight
    : direction === "left"
      ? ChevronLeft
      : ChevronRight;

  // Use disabled variant if disabled prop is true, otherwise use the provided variant
  const effectiveVariant = disabled ? "disabled" : variant;

  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[effectiveVariant]} ${className}`}
      {...props}
    >
      <IconComponent className="w-4 h-4" />
    </button>
  );
};

NavigationButton.propTypes = {
  direction: PropTypes.oneOf(["left", "right"]).isRequired,
  double: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["primary", "secondary", "outline", "disabled"]),
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default NavigationButton;
